import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../image/mg.png";
import { useSelector } from "react-redux";
import { selectCartCount } from "../redux/slice/CartSlice";
import MiniCart from "./MiniCart";

const navLinks = [
  { label: "Home",            to: "/",                    icon: "bi-house-fill",       exact: true  },
  { label: "Gyms",            to: "/varanasi/gym",        icon: "bi-buildings-fill",   endsWith: "/gym" },
  { label: "Fitness Trainer", to: "/gym-fitness-trainer", icon: "bi-person-workspace",  startsWith: "/gym-fitness-trainer" },
  { label: "Shop",            to: "/shop",                icon: "bi-bag-fill",         startsWith: "/shop" },
  { label: "Blog",            to: "/blog",                icon: "bi-journal-richtext", startsWith: "/blog" },
  { label: "About Us",        to: "/about-us",            icon: "bi-info-circle-fill", exact: "/about-us" },
  { label: "Contact Us",      to: "/contact-us",          icon: "bi-chat-dots-fill",   exact: "/contact-us" },
];

const serviceLinks = [
  { label: "List Your Gym",    to: "/free-gym-listing",                  icon: "bi-building-add" },
  { label: "Gym Setup",        to: "/gym-setup-and-design-work",         icon: "bi-tools" },
  { label: "Gym Software",     to: "/gym-management-software",           icon: "bi-laptop-fill" },
  { label: "Gym Membership",   to: "/gym-membership-discounts-coupon",   icon: "bi-tag-fill" },
];

export default function Navbar() {
  const cartCount = useSelector(selectCartCount);
  const location  = useLocation();

  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled,     setScrolled]     = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close services accordion when route changes
  useEffect(() => { setServicesOpen(false); }, [location.pathname]);

  const isActive = (link) => {
    if (link.exact === true)       return location.pathname === "/";
    if (link.exact)                return location.pathname === link.exact;
    if (link.endsWith)             return location.pathname.endsWith(link.endsWith);
    if (link.startsWith)          return location.pathname.startsWith(link.startsWith);
    return false;
  };

  const isServiceActive = serviceLinks.some(s => location.pathname === s.to);

  const closeOffcanvas = () => {
    const el = document.getElementById("collapsibleNavbar");
    if (el && window.bootstrap?.Offcanvas) {
      const instance = window.bootstrap.Offcanvas.getInstance(el);
      instance?.hide();
    }
  };

  return (
    <>
      <div className="top_bar">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <p className="m-0">
                <i className="bi bi-house-door-fill me-1"></i>Welcome to Mera Gym, Start your fitness journey with us.
              </p>
            </div>
            <div className="col-md-4">
              <ul>
                <li>
                  <a href="https://app.meragym.com/" target="_blank" rel="noreferrer">
                    <i className="fa-solid fa-dumbbell"></i>Get Gym Software
                  </a>
                </li>
                <li>
                  <a href="/login">
                    <i className="fa-solid fa-user"></i>Trainer Login
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <header className={`header${scrolled ? " header-scrolled" : ""}`}>
        <nav className="navbar navbar-expand-lg nav-top">
          <div className="container nav-container">
            {/* Logo */}
            <Link to="/" className="brand-link">
              <img className="mg navbar-brand" src={logo} alt="MeraGym - Find Gyms Near You" />
            </Link>

            {/* Mobile: cart + hamburger */}
            <div className="nav-mobile-actions d-flex d-lg-none align-items-center gap-2">
              <button
                className="mg-cart-icon-btn"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#miniCartOffcanvas"
                aria-controls="miniCartOffcanvas"
                aria-label="Open cart"
              >
                <i className="fas fa-shopping-cart"></i>
                {cartCount > 0 && <span className="mg-cart-badge">{cartCount}</span>}
              </button>
              <button
                className="navbar-toggler border-0"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#collapsibleNavbar"
                aria-controls="collapsibleNavbar"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>

            {/* Offcanvas */}
            <div className="offcanvas-lg offcanvas-start custom-offcanvas" tabIndex="-1" id="collapsibleNavbar">

              {/* Sidebar Header */}
              <div className="sidebar-header">
                <div className="sidebar-logo-wrap">
                  <img src={logo} alt="MeraGym - Find Gyms Near You" className="sidebar-logo" />
                </div>
                <button
                  className="sidebar-close-btn"
                  type="button"
                  data-bs-dismiss="offcanvas"
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <div className="offcanvas-body d-flex flex-column flex-lg-row align-items-center">
                <ul className="navbar-nav ms-auto sidebar-nav-list">

                  {/* Regular nav links */}
                  {navLinks.map((link) => (
                    <li className="nav-item navItem" key={link.to}>
                      <Link
                        to={link.to}
                        className={`nav-link sidebar-nav-link${isActive(link) ? " active" : ""}`}
                        onClick={closeOffcanvas}
                      >
                        <i className={`bi ${link.icon} sidebar-nav-icon`}></i>
                        {link.label}
                        {isActive(link) && <span className="sidebar-active-dot"></span>}
                      </Link>
                    </li>
                  ))}

                  {/* Services — accordion on mobile, hover/focus dropdown on desktop */}
                  <li className="nav-item navItem nav-item-services">
                    {/* Desktop: keyboard-accessible trigger */}
                    <button
                      type="button"
                      className={`nav-link nav-drop sidebar-nav-link d-none d-lg-flex${isServiceActive ? " active" : ""}`}
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      Services
                      <i className="bi bi-chevron-down ms-1" style={{ fontSize: "0.7rem" }}></i>
                    </button>

                    {/* Mobile: accordion toggle */}
                    <button
                      className={`sidebar-services-toggle d-lg-none${isServiceActive ? " active" : ""}${servicesOpen ? " open" : ""}`}
                      onClick={() => setServicesOpen(o => !o)}
                      aria-expanded={servicesOpen}
                    >
                      <span className="sidebar-services-left">
                        <i className="bi bi-grid-fill sidebar-nav-icon"></i>
                        Services
                      </span>
                      <i className={`bi bi-chevron-down sidebar-chevron${servicesOpen ? " rotated" : ""}`}></i>
                    </button>

                    {/* Desktop dropdown */}
                    <ul className="nav-dropdown">
                      {serviceLinks.map(s => (
                        <li key={s.to}><Link to={s.to}>{s.label}</Link></li>
                      ))}
                    </ul>

                    {/* Mobile accordion body */}
                    <div className={`sidebar-accordion${servicesOpen ? " open" : ""}`}>
                      {serviceLinks.map(s => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className={`sidebar-acc-link${location.pathname === s.to ? " active" : ""}`}
                          onClick={closeOffcanvas}
                        >
                          <i className={`bi ${s.icon}`}></i>
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </li>
                </ul>

                {/* Desktop: Cart */}
                <button
                  className="mg-cart-icon-btn d-none d-lg-flex ms-lg-4"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#miniCartOffcanvas"
                  aria-controls="miniCartOffcanvas"
                  aria-label="Open cart"
                >
                  <i className="fas fa-shopping-cart"></i>
                  {cartCount > 0 && <span className="mg-cart-badge">{cartCount}</span>}
                </button>
              </div>

              {/* Mobile bottom buttons (Sticky Footer) */}
              <div className="nav-mob-actions d-lg-none">
                <a href="https://app.meragym.com/" target="_blank" rel="noreferrer" className="nav-mob-btn nav-mob-btn-secondary">
                  <i className="fa-solid fa-dumbbell me-2"></i>Get Gym Software
                </a>
                <a href="/login" className="nav-mob-btn nav-mob-btn-primary" onClick={closeOffcanvas}>
                  <i className="fa-solid fa-user me-2"></i>Trainer Login
                </a>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <MiniCart />
    </>
  );
}
